import fromPairs from "ramda/src/fromPairs";
import ascend from "ramda/src/ascend";
import identity from "ramda/src/identity";
import indexBy from "ramda/src/indexBy";
import sort from "ramda/src/sort";
import * as React from "react";
import { useCallback } from "react";
import {
  FilterQuery,
  ParameterAndFilter,
  ParameterFilter,
  ParameterFilterOption,
  ParameterOrFilter,
  ParameterRangeFilter,
  TextParameterFilterOption,
} from "../../../../../../microservices/functions/src/types/product_catalog/catalog_api_types";
import { AppliedFilter } from "../applied_filter/applied_filter";
import { Filter } from "../applied_filter/filter";
import {
  removeRangeParameterFilter,
  removeTextParameterFilter,
} from "../helpers/handle_remove_filter";

const filterKey: keyof FilterQuery = "parameters";
const serializedKey = "parametry";

// 50[60-70]
const rangeParameterRegexp = /^(\d+)\[(\d+)-(\d+)]$/;

// 60[+100|105|106]
const andParameterRegexp = /^(\d+)\[\*((\d+\|)*\d+)]$/;

// 70[210|215|220]
const orParameterRegexp = /^(\d+)\[((\d+\|)*\d+)]$/;

const sortValueIds = sort(ascend(identity));

const sortById = sort(
  ascend(([parameterId]: [string, ...any]) => parseInt(parameterId))
);

const deserializeValueIds = (serializedValuedIds: string): number[] =>
  sortValueIds(
    serializedValuedIds.split("|").map((valueId) => parseInt(valueId))
  );

export const ParametersFilter: Filter = {
  key: "parameters",
  serialize: (appliedFilter) => {
    if (appliedFilter.parameters === undefined) {
      return null;
    }
    const serializedValues = sortById(Object.entries(appliedFilter.parameters))
      .filter(
        ([, filter]) => filter.type === "range" || filter.valueIds.length > 0
      )
      .map(([parameterId, filter]) => {
        if (filter.type === "range") {
          return `${parameterId}[${filter.range.gte}-${filter.range.lte}]`;
        } else if (filter.type === "or") {
          return `${parameterId}[${sortValueIds(filter.valueIds).join("|")}]`;
        } else if (filter.type === "and") {
          return `${parameterId}[*${sortValueIds(filter.valueIds).join("|")}]`;
        }
      })
      .filter(Boolean);

    return serializedValues.length > 0
      ? `${serializedKey}=${serializedValues.join(",")}`
      : null;
  },
  deserialize: (urlSearchParams: URLSearchParams) => {
    const serializedValues = urlSearchParams.get(serializedKey);

    if (serializedValues === null) {
      return;
    }

    const deserializedValues = serializedValues
      .split(",")
      .map((serializedValue): [number, ParameterFilter] | undefined => {
        const rangeMatch = serializedValue.match(rangeParameterRegexp);
        if (rangeMatch) {
          return [
            parseInt(rangeMatch[1]),
            {
              type: "range",
              range: {
                gte: parseInt(rangeMatch[2]),
                lte: parseInt(rangeMatch[3]),
              },
            },
          ];
        }

        const andMatch = serializedValue.match(andParameterRegexp);
        if (andMatch) {
          return [
            parseInt(andMatch[1]),
            {
              type: "and",
              valueIds: deserializeValueIds(andMatch[2]),
            },
          ];
        }

        const orMatch = serializedValue.match(orParameterRegexp);
        if (orMatch) {
          return [
            parseInt(orMatch[1]),
            {
              type: "or",
              valueIds: deserializeValueIds(orMatch[2]),
            },
          ];
        }
      })
      .filter(
        (deserializedValue): deserializedValue is [number, ParameterFilter] =>
          deserializedValue !== undefined
      );

    return deserializedValues.length > 0
      ? {
          parameters: fromPairs(deserializedValues),
        }
      : undefined;
  },
  AppliedControlComponent: ({ appliedFilter, filterOptions, onRemove }) => {
    const handleRemoveTextParameterFilter = useCallback(
      (parameterId: number, valueId: number) => {
        onRemove({
          ...appliedFilter,
          parameters: removeTextParameterFilter(
            appliedFilter.parameters,
            parameterId,
            valueId
          ),
        });
      },
      [onRemove, appliedFilter]
    );
    const handleRemoveRangeParameterFilter = useCallback(
      (parameterId: number) => {
        onRemove({
          ...appliedFilter,
          parameters: removeRangeParameterFilter(
            appliedFilter.parameters,
            parameterId
          ),
        });
      },
      [onRemove, appliedFilter]
    );

    const parametersFilter = appliedFilter[filterKey];

    if (parametersFilter === undefined) {
      return null;
    }

    const validAppliedFilters = sort(
      ascend(
        (key) => filterOptions.parametersById[parseInt(key)]?.sortOrderInFilter
      ),
      Object.keys(parametersFilter)
    )
      .map(
        (parameterId) =>
          filterOptions.parametersById[
            parseInt(parameterId)
          ] as ParameterFilterOption
      )
      .filter(Boolean);

    return (
      <>
        {validAppliedFilters.map((parameter) => {
          const appliedFilter = parametersFilter[parameter.id];

          if (appliedFilter.type === "range") {
            return (
              <AppliedRangeParameterFilter
                key={parameter.id}
                parameter={parameter}
                appliedFilter={appliedFilter}
                onRemove={handleRemoveRangeParameterFilter}
              />
            );
          } else {
            return (
              <AppliedTextParameterFilter
                key={parameter.id}
                parameter={parameter}
                allValues={filterOptions.allTextParameters[parameter.id]}
                appliedFilter={appliedFilter}
                onRemove={handleRemoveTextParameterFilter}
              />
            );
          }
        })}
      </>
    );
  },
};

type AppliedRangeParameterFilterProps = {
  parameter: ParameterFilterOption;
  appliedFilter: ParameterRangeFilter;
  onRemove: (parameterId: number) => void;
};

const AppliedRangeParameterFilter = React.memo(
  ({
    appliedFilter,
    parameter,
    onRemove,
  }: AppliedRangeParameterFilterProps): JSX.Element | null => {
    const unitString = parameter.unit === null ? "" : " " + parameter.unit;
    const name = `od ${appliedFilter.range.gte}${unitString} do ${appliedFilter.range.lte}${unitString}`;

    return (
      <AppliedFilter
        filterName={parameter.name}
        values={[{ name }]}
        onRemove={() => onRemove(parameter.id)}
      />
    );
  }
);

type AppliedTextParameterFilterProps = {
  parameter: ParameterFilterOption;
  allValues: TextParameterFilterOption[];
  appliedFilter: ParameterAndFilter | ParameterOrFilter;
  onRemove: (parameterId: number, valueId: number) => void;
};

const AppliedTextParameterFilter = React.memo(
  ({
    allValues,
    appliedFilter,
    parameter,
    onRemove,
  }: AppliedTextParameterFilterProps): JSX.Element | null => {
    if (
      appliedFilter.valueIds === undefined ||
      appliedFilter.valueIds.length === 0
    ) {
      return null;
    }

    const valuesById = indexBy(
      (value) => value.id,
      allValues.map((value, index) => ({
        id: value.i,
        index: index,
        name: value.v,
      }))
    );

    const sortedAppliedValues = sort(
      ascend((value) => value.index),
      appliedFilter.valueIds
        .map((valueId) => valuesById[valueId])
        .filter(Boolean)
    );

    return (
      <AppliedFilter
        filterName={parameter.name}
        values={sortedAppliedValues}
        onRemove={(valueId) => onRemove(parameter.id, valueId as number)}
      />
    );
  }
);
